import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="AutomateSEO.ai" className="h-11 w-auto" />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="flex items-center gap-3 mb-4">
          <Cookie className="w-10 h-10 text-blue-600" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Cookie Policy</h1>
        </div>
        <p className="text-gray-600 mb-8">Last Updated: December 7, 2025</p>
        
        <div className="prose prose-lg max-w-none space-y-8">
          <section className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-500">
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>TL;DR:</strong> We use cookies to keep you logged in, remember your preferences, and improve our platform. No sneaky tracking or selling your data. You're always in control.
            </p>
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> We offer a 3-day free trial for new users. See our Refund Policy for details on refunds (available within 3 days of payment).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files stored on your device when you visit AutomateSEO.ai. They help us recognize you, remember your preferences, and provide a seamless experience across sessions.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Think of cookies like a hotel key card that remembers your room number and preferences - except for websites. They make your experience smoother and more personalized.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Types of Cookies We Use</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">1. Essential Cookies (Required)</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  These cookies are necessary for the platform to function. Without them, you can't log in or use AutomateSEO.ai.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Authentication:</strong> Keeps you logged in between pages</li>
                  <li><strong>Security:</strong> Protects against fraud and unauthorized access</li>
                  <li><strong>Load Balancing:</strong> Distributes traffic for optimal performance</li>
                  <li><strong>Session Management:</strong> Maintains your active session</li>
                </ul>
                <p className="text-gray-600 italic mt-3 text-sm">Cannot be disabled - required for basic functionality</p>
              </div>

              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">2. Functional Cookies (Recommended)</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  These remember your preferences and make AutomateSEO.ai work the way you like.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Language Preferences:</strong> Remember your selected language</li>
                  <li><strong>Theme Settings:</strong> Save your UI customizations</li>
                  <li><strong>Dashboard Layout:</strong> Remember your widget arrangements</li>
                  <li><strong>Notification Preferences:</strong> Store your alert settings</li>
                  <li><strong>Timezone:</strong> Display times in your local timezone</li>
                </ul>
                <p className="text-gray-600 italic mt-3 text-sm">Can be disabled, but you'll lose personalization</p>
              </div>

              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">3. Analytics Cookies (Optional)</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Help us understand how you use the platform so we can improve it.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Usage Patterns:</strong> Which features are most popular</li>
                  <li><strong>Performance Metrics:</strong> Page load times and errors</li>
                  <li><strong>User Flow:</strong> How users navigate the platform</li>
                  <li><strong>Feature Adoption:</strong> Which new features get used</li>
                </ul>
                <p className="text-gray-600 italic mt-3 text-sm">All data is anonymized - we never track individuals</p>
              </div>

              <div className="border-l-4 border-orange-500 pl-6">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">4. Performance Cookies (Optional)</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Optimize platform speed and reliability based on your usage.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Caching:</strong> Speed up page loads by storing data locally</li>
                  <li><strong>CDN Selection:</strong> Serve content from nearest server</li>
                  <li><strong>Resource Optimization:</strong> Pre-load frequently used features</li>
                  <li><strong>Error Recovery:</strong> Maintain stability during issues</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">We DON'T Use:</h2>
            <div className="bg-red-50 p-6 rounded-lg">
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Advertising Cookies:</strong> We don't track you for ads</li>
                <li><strong>Third-Party Tracking:</strong> No Facebook Pixel, Google Ads tracking, etc.</li>
                <li><strong>Cross-Site Tracking:</strong> We don't follow you around the internet</li>
                <li><strong>Behavioral Profiling:</strong> No creepy data mining or profiling</li>
                <li><strong>Data Selling:</strong> Your cookie data is never sold to anyone</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Cookie Lifespan</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 border-b">Cookie Type</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 border-b">Duration</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 border-b">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Session</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Until you log out</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Keep you logged in</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Remember Me</td>
                    <td className="px-6 py-4 text-sm text-gray-700">30 days</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Auto-login on return</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Preferences</td>
                    <td className="px-6 py-4 text-sm text-gray-700">1 year</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Remember your settings</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Analytics</td>
                    <td className="px-6 py-4 text-sm text-gray-700">90 days</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Usage patterns</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Managing Your Cookie Preferences</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-gray-800">In AutomateSEO.ai</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Control cookies directly in your account settings:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2 mb-6">
              <li>Go to <strong>Account Settings</strong></li>
              <li>Navigate to <strong>Privacy & Cookies</strong></li>
              <li>Toggle cookie categories on/off</li>
              <li>Save your preferences</li>
            </ol>

            <h3 className="text-xl font-semibold mb-3 text-gray-800">In Your Browser</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              All browsers let you control cookies. Here's how:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Chrome:</strong> Settings → Privacy & Security → Cookies</li>
              <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
              <li><strong>Edge:</strong> Settings → Privacy → Cookies</li>
            </ul>
            <p className="text-gray-600 italic mt-4 text-sm">
              Note: Blocking all cookies will prevent you from using AutomateSEO.ai as essential cookies are required for login and security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use minimal third-party services, and they may set their own cookies:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Razorpay/Stripe:</strong> Payment processing (only on payment pages)</li>
              <li><strong>Google Analytics:</strong> Anonymized usage data (if analytics enabled)</li>
              <li><strong>AWS CloudFront:</strong> Content delivery for faster loading</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Each service has its own cookie policy. We only work with GDPR-compliant providers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Do Not Track (DNT)</h2>
            <p className="text-gray-700 leading-relaxed">
              We respect the Do Not Track browser setting. When DNT is enabled, we disable all optional cookies automatically and limit data collection to essential functionality only.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Cookie Consent</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you first visit AutomateSEO.ai, we'll ask for your cookie consent. You can:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Accept All:</strong> Enable all cookie types for best experience</li>
              <li><strong>Essential Only:</strong> Use only required cookies</li>
              <li><strong>Customize:</strong> Choose which cookie types to enable</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You can change your choice anytime in account settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Updates to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Cookie Policy as our platform evolves or regulations change. Significant changes will be communicated via email 30 days in advance. Continued use after changes indicates acceptance.
            </p>
          </section>

          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Questions About Cookies?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We're here to help you understand how we use cookies.
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> privacy@automateseo.ai<br/>
              <strong>Support:</strong> support@automateseo.ai<br/>
              <strong>Phone:</strong> +91 9810535398
            </p>
          </section>

          <section className="border-l-4 border-green-500 pl-6">
            <p className="text-gray-700 leading-relaxed italic">
              "We believe in transparency. Cookies help us make AutomateSEO.ai work better for you, but you're always in control of your data." - AutomateSEO.ai Team
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;